import React, { useEffect, useState } from "react";
import {
  listContributionTypes,
  createContributionType,
  updateContributionType,
} from "../../services/api";
import toast from "react-hot-toast";

export default function ContributionTypeModal({ onClose }) {
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await listContributionTypes();
      setTypes(res.data || []);
    } catch {
      toast.error("Failed to load contribution types");
    } finally {
      setLoading(false);
    }
  };

  const addType = async () => {
    const formattedName = newType.trim().toUpperCase();

    if (!formattedName) {
      toast.error("Enter contribution type name");
      return;
    }

    const exists = types.some((t) => t.name.toUpperCase() === formattedName);
    if (exists) {
      toast.error("Contribution type already exists");
      return;
    }

    try {
      setAdding(true);
      await createContributionType({ name: formattedName });
      toast.success("Contribution type added");
      setNewType("");
      load();
    } catch {
      toast.error("Failed to add type");
    } finally {
      setAdding(false);
    }
  };

  const toggleStatus = async (type) => {
    try {
      await updateContributionType(type.type_id, {
        name: type.name,
        isRecurring: type.is_recurring === 1,
        status: type.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
      });
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal type-modal-modern">
        {/* HEADER */}
        <div className="modal-header-modern">
          <h3>Contribution Types</h3>
          <p className="muted">Manage available offering categories</p>
        </div>

        {/* SCROLLABLE TABLE */}
        <div className="type-table-wrapper">
          <div className="type-table">
            <div className="type-table-header">
              <div className="type-col-name">Name</div>
              <div className="type-col-status">Status</div>
              <div className="type-col-action">Action</div>
            </div>

            {loading ? (
              <div className="type-row" style={{ opacity: 0.6 }}>
                Loading contribution types...
              </div>
            ) : (
              types.map((t) => (
                <div key={t.type_id} className="type-row">
                  <div className="type-col-name">{t.name}</div>

                  <div className="type-col-status">
                    <span
                      className={
                        t.status === "ACTIVE" ? "badge-success" : "badge-danger"
                      }
                    >
                      {t.status}
                    </span>
                  </div>

                  <div className="type-col-action">
                    <button
                      className="btn-outline small"
                      onClick={() => toggleStatus(t)}
                    >
                      {t.status === "ACTIVE" ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="type-footer-row">
          <div className="type-col-name">
            <input
              className="type-input-modern"
              placeholder="Add new type (e.g. ZAKA)"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
            />
          </div>

          <div className="type-col-status"></div>

          <div className="type-col-action type-footer-actions">
            <button
              className="btn-purple"
              onClick={addType}
              disabled={adding}
            >
              {adding ? "Adding..." : "Add Type"}
            </button>

            <button className="btn-outline" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* INLINE CSS */}
      <style>{`
        .modal-overlay { 
          position: fixed; inset: 0; 
          background: rgba(15,23,42,0.55); 
          backdrop-filter: blur(4px); 
          display: flex; align-items: center; justify-content: center; 
          z-index: 999; 
        }

        .type-modal-modern { 
          width: 720px; max-width: 95%; 
          background: #fff; border-radius: 12px; 
          box-shadow: 0 20px 45px rgba(0,0,0,0.08); 
          display: flex; flex-direction: column; 
          overflow: hidden; font-family: "Inter", sans-serif; 
        }

        .modal-header-modern { 
          padding: 20px 24px 12px; 
          border-bottom: 1px solid #eef2f7; 
          flex-shrink: 0;
        }
        .modal-header-modern h3 { margin:0; font-size:18px; font-weight:600; color:#111827; }
        .modal-header-modern .muted { margin-top:4px; font-size:13px; color:#6b7280; }

        .type-table-wrapper {
          max-height: 400px; /* Adjust as needed */
          overflow-y: auto;
        }
        .type-table-wrapper::-webkit-scrollbar {
          width: 6px;
        }
        .type-table-wrapper::-webkit-scrollbar-thumb {
          background-color: rgba(0,0,0,0.2);
          border-radius: 3px;
        }

        .type-table { padding: 12px 0; }

        .type-table-header { 
          display: grid; grid-template-columns: 2fr 1fr 1fr; 
          padding: 10px 24px; font-size: 12px; font-weight: 600; 
          color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; 
        }

        .type-row { 
          display: grid; grid-template-columns: 2fr 1fr 1fr; 
          padding: 14px 24px; align-items: center; border-top: 1px solid #f1f5f9; 
          transition: background 0.2s ease; 
        }
        .type-row:hover { background:#f9fafb; }

        .type-col-name { font-weight:500; color:#111827; }
        .type-col-status { text-align:left; }
        .type-col-action { text-align:right; }

        .badge-success { background:#ecfdf5; color:#059669; font-size:12px; padding:4px 10px; border-radius:999px; font-weight:500; }
        .badge-danger { background:#fef2f2; color:#dc2626; font-size:12px; padding:4px 10px; border-radius:999px; font-weight:500; }

        .type-footer-row { 
          display: grid; grid-template-columns: 2fr 1fr 1fr; 
          padding: 16px 24px; border-top: 1px solid #eef2f7; background:#fafafa; align-items:center; 
          flex-shrink: 0;
        }

        .type-input-modern { 
          width:100%; padding:10px 12px; border-radius:8px; border:1px solid #e5e7eb; font-size:14px; transition: all 0.2s ease; 
        }
        .type-input-modern:focus { outline:none; border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,0.15); }

        button { cursor:pointer; font-size:13px; font-weight:500; border-radius:8px; transition:all 0.2s ease; }
        .btn-purple { background:#7c3aed; color:white; border:none; padding:10px 14px; }
        .btn-purple:hover { background:#6d28d9; }
        .btn-outline { background:white; border:1px solid #d1d5db; padding:9px 12px; color:#374151; }
        .btn-outline:hover { background:#f9fafb; }
        .btn-outline.small { padding:6px 10px; font-size:12px; }
        .type-footer-actions { display:flex; justify-content:flex-end; gap:10px; }
      `}</style>
    </div>
  );
}